import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, X } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "savnt-music-state";

/**
 * Custom sticky bottom music bar for the SAVNT Apple Music playlist.
 *
 * Plays 30-second public preview clips on infinite loop. No Apple branding,
 * no recommendations, no large player UI — just album art, title, artist,
 * play/pause, progress, volume, prev/next, and a dismiss button. Persistent
 * across navigation because the component is mounted at App level outside
 * <Routes>.
 *
 * Browsers block autoplay until the visitor interacts with the page, so the
 * default state is paused and labelled "Tap to play."
 */
export const CustomMusicBar = () => {
    const [tracks, setTracks] = useState([]);
    const [index, setIndex] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(0.6);
    const [muted, setMuted] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const audioRef = useRef(null);

    // Restore persisted user preferences (volume, dismissed) ----------------
    useEffect(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) {
                const s = JSON.parse(raw);
                if (typeof s.volume === "number") setVolume(s.volume);
                if (typeof s.muted === "boolean") setMuted(s.muted);
                if (typeof s.dismissed === "boolean") setDismissed(s.dismissed);
            }
        } catch (err) {
            console.warn("MusicBar: failed to read storage", err);
        }
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ volume, muted, dismissed }));
        } catch (err) {
            console.warn("MusicBar: failed to persist storage", err);
        }
    }, [volume, muted, dismissed]);

    // Fetch the playlist once on mount --------------------------------------
    useEffect(() => {
        axios
            .get(`${BACKEND}/api/music/playlist`)
            .then(({ data }) => setTracks(data.tracks || []))
            .catch((e) => console.warn("MusicBar: playlist fetch failed", e));
    }, []);

    // Wire volume + mute to the actual audio element ------------------------
    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = volume;
            audioRef.current.muted = muted;
        }
    }, [volume, muted]);

    const current = tracks[index];

    // Audio event handlers --------------------------------------------------
    const onTimeUpdate = useCallback((e) => {
        const el = e.currentTarget;
        setProgress(el.currentTime);
        setDuration(el.duration || 0);
    }, []);

    const next = useCallback(() => {
        if (!tracks.length) return;
        setIndex((i) => (i + 1) % tracks.length);
    }, [tracks.length]);

    const prev = useCallback(() => {
        if (!tracks.length) return;
        setIndex((i) => (i - 1 + tracks.length) % tracks.length);
    }, [tracks.length]);

    // Auto-advance + auto-play when track changes ---------------------------
    useEffect(() => {
        if (!audioRef.current || !current) return;
        audioRef.current.load();
        if (playing) {
            audioRef.current.play().catch(() => setPlaying(false));
        }
    }, [index, current, playing]);

    const onEnded = useCallback(() => {
        next();
    }, [next]);

    const togglePlay = useCallback(async () => {
        if (!audioRef.current || !current) return;
        if (playing) {
            audioRef.current.pause();
            setPlaying(false);
        } else {
            try {
                await audioRef.current.play();
                setPlaying(true);
            } catch (err) {
                console.warn("MusicBar: play blocked", err);
            }
        }
    }, [playing, current]);

    const onSeek = useCallback((e) => {
        const el = audioRef.current;
        if (!el || !duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const pct = (e.clientX - rect.left) / rect.width;
        el.currentTime = Math.max(0, Math.min(duration, pct * duration));
    }, [duration]);

    if (dismissed) return null;
    if (!current) return null;

    const pct = duration ? (progress / duration) * 100 : 0;

    return (
        <div
            data-testid="custom-music-bar"
            className="fixed left-0 right-0 bottom-0 z-[60] pointer-events-none"
            style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
            <div className="pointer-events-auto mx-auto max-w-2xl border-t border-x border-white/[0.06] bg-[#0C0C0C]/95 backdrop-blur-xl shadow-[0_-8px_24px_rgba(0,0,0,0.35)]">
                {/* Slim progress bar across the top */}
                <button
                    type="button"
                    onClick={onSeek}
                    data-testid="music-seek"
                    aria-label="Seek"
                    className="block w-full h-[2px] bg-white/[0.06] cursor-pointer hover:bg-white/[0.10] transition-colors"
                >
                    <div
                        className="h-full bg-[#EDEDE6]/80 transition-[width] duration-150"
                        style={{ width: `${pct}%` }}
                    />
                </button>

                <div className="flex items-center gap-3 px-3 sm:px-4 h-[44px]">
                    {/* Album art */}
                    {current.artwork ? (
                        <img
                            src={current.artwork}
                            alt=""
                            className="h-7 w-7 object-cover rounded-[2px] flex-shrink-0"
                        />
                    ) : (
                        <div className="h-7 w-7 bg-white/[0.06] rounded-[2px] flex-shrink-0" />
                    )}

                    {/* Title + artist */}
                    <div className="min-w-0 flex-1 leading-tight">
                        <div className="text-[12px] text-[#EDEDE6] truncate font-display" data-testid="music-title">
                            {current.title}
                        </div>
                        <div className="text-[10px] tracking-[0.12em] uppercase text-[#7A7A75] truncate" data-testid="music-artist">
                            {current.artist}
                        </div>
                    </div>

                    {/* Transport controls */}
                    <div className="flex items-center gap-0.5">
                        <button
                            type="button"
                            onClick={prev}
                            data-testid="music-prev"
                            aria-label="Previous track"
                            className="h-7 w-7 grid place-items-center text-[#9A9A92] hover:text-[#EDEDE6] transition-colors hidden sm:grid"
                        >
                            <SkipBack className="h-3.5 w-3.5" />
                        </button>
                        <button
                            type="button"
                            onClick={togglePlay}
                            data-testid="music-play"
                            aria-label={playing ? "Pause" : "Play"}
                            className="h-8 w-8 grid place-items-center rounded-full bg-[#EDEDE6] text-[#0C0C0C] hover:bg-white transition-colors"
                        >
                            {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 translate-x-[1px]" />}
                        </button>
                        <button
                            type="button"
                            onClick={next}
                            data-testid="music-next"
                            aria-label="Next track"
                            className="h-7 w-7 grid place-items-center text-[#9A9A92] hover:text-[#EDEDE6] transition-colors hidden sm:grid"
                        >
                            <SkipForward className="h-3.5 w-3.5" />
                        </button>
                    </div>

                    {/* Volume slider (desktop only) */}
                    <div className="hidden md:flex items-center gap-1.5 pl-2 border-l border-white/[0.06] ml-1">
                        <button
                            type="button"
                            onClick={() => setMuted((m) => !m)}
                            data-testid="music-mute"
                            aria-label={muted ? "Unmute" : "Mute"}
                            className="h-6 w-6 grid place-items-center text-[#9A9A92] hover:text-[#EDEDE6] transition-colors"
                        >
                            {muted || volume === 0 ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
                        </button>
                        <input
                            type="range"
                            min={0}
                            max={1}
                            step={0.01}
                            value={muted ? 0 : volume}
                            onChange={(e) => {
                                const v = parseFloat(e.target.value);
                                setVolume(v);
                                if (v > 0) setMuted(false);
                            }}
                            data-testid="music-volume"
                            aria-label="Volume"
                            className="w-16 accent-[#EDEDE6] cursor-pointer"
                        />
                    </div>

                    {/* Dismiss */}
                    <button
                        type="button"
                        onClick={() => setDismissed(true)}
                        data-testid="music-close"
                        aria-label="Close player"
                        className="h-6 w-6 grid place-items-center text-[#5A5A55] hover:text-[#EDEDE6] transition-colors"
                    >
                        <X className="h-3 w-3" />
                    </button>
                </div>

                {/* Hidden audio element */}
                <audio
                    ref={audioRef}
                    src={current.preview}
                    onTimeUpdate={onTimeUpdate}
                    onEnded={onEnded}
                    preload="auto"
                />
            </div>
        </div>
    );
};

export default CustomMusicBar;
